import React, { type ReactNode, useEffect, useState } from 'react'
import LoadingPageV2 from '../LoadingPageV2'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import CustomHeader from '../../components/CustomHeader'
import PAGE_TITLES from '../../consts/titles'
import ReportsFeature from '../../features/ReportsFeatureV2'
import ROUTES from '../../consts/paths'
import SkipNavigation from '../../components/SkipNavigation'
import { notification } from '../../components/Notifications'
import { Box, Container, Stack } from '@mui/material'
import EmptyListCard from '../../features/InstructorDashboardFeature/components/EmptyListCard'
import ReportsControls from '../../features/ReportsFeatureV2/components/ReportsControls'
import PageHeader from '../../components/PageHeader'
import { prepareBreadCrumbs } from '../../utils/BreadcrumbDataPreparation'
import ReportAttributes from '../../features/ReportsFeatureV2/components/ReportAttributes'
import TermHeader from '../../components/TermHeader'
import { DistributionManager, DivisionManager, TermsManager } from '../../APIManager'
import { useMutation, useQuery } from '@tanstack/react-query'
import { saveAs } from 'file-saver'
import { getRoles } from '../../store/slices/roleSlice/selectors'
import { formatReportPayload, loadDisplaySettings } from '../../features/ReportsFeatureV2/components/helper'
import { getDefaultSettings } from '../../features/ReportsFeatureV2/components/DisplayCustomizationDrawer'
import { formatErrorMessage } from '../../helpers/errorResponseGenerate'
import { HttpStatusCode } from '../../consts/httpStatusCode'

const ReportsPage: React.FC = (): JSX.Element => {
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false)
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  // const { settings, setSettings } = useDisplaySettings()
  const seriesId = searchParams.get('seriesId')
  const termId = searchParams.get('termId')
  const params = { termId, distributionSeriesId: seriesId, pageSize: 200 }
  const [initialLoading, setInitialLoading] = useState<boolean>(true)
  const termsParams = {
    distributionSeriesId: seriesId,
    isReport: true
  }
  const settings = loadDisplaySettings(getDefaultSettings)

  const divisionAndSections = getRoles()
  const navigate = useNavigate()

  useEffect(() => {
    const validDivisions = divisionAndSections?.filter(division => division?.division)
    const firstDivisionId = validDivisions?.[0]?.division?.divisionId ?? null

    if (!id && firstDivisionId) {
      navigate({
        pathname: `${ROUTES.REPORTS}/${firstDivisionId}`,
        search: searchParams.toString()
      })
    }
  }, [divisionAndSections])

  //  * On division ID change, reset the 'comparisons' field in the displaySettings stored in localStorage.
  //  *
  //  * This ensures that when the user navigates to a different division, any previous comparison selections
  //  * are cleared and set to the default state ({ compareBy: null, comparison: null }).
  useEffect(() => {
    if (id) {
      const stored = localStorage.getItem('displaySettings')
      if (stored) {
        try {
          const settings = JSON.parse(stored)
          settings.comparisons = [{ compareBy: null, comparison: null }]
          localStorage.setItem('displaySettings', JSON.stringify(settings))
        } catch {}
      }
    }
  }, [id])

  const { data: breadCrumbData, isLoading: breadcrumbLoading } = useQuery({
    queryKey: ['breadcrumbs', id],
    queryFn: async () => await DivisionManager.getBreadCrumbList(id ?? '')
  })

  const preparedBreadCrumbList = prepareBreadCrumbs(breadCrumbData ?? [], ROUTES.REPORTS, searchParams)
  const divisionId = breadCrumbData?.[breadCrumbData?.length - 1].id ?? ''

  const { data: distributionSeries = [], isLoading: distributionSeriesLoading } = useQuery({
    queryKey: ['distributionSeries'],
    staleTime: 10000,
    queryFn: async () => (await DistributionManager.getAllDistributionSeries()).data,
    retry: false,
    enabled: true
  })

  const { data: terms = [], isLoading: termsLoading } = useQuery({
    queryKey: ['terms', seriesId, id],
    staleTime: 10000,
    queryFn: async () => (await TermsManager.getAllTerms(termsParams)).data,
    retry: false,
    enabled: !!seriesId
  })

  const { mutate: fetchReports, data: reports = [], error: reportError, isError: isReportsError, isPending } = useMutation({
    mutationFn: async (payload?: Record<string, any>) => {
      const calc = Object.keys(payload ?? {}).length > 0 ? payload : formatReportPayload(settings)
      const safeData = calc ?? {}
      const res = await DivisionManager.getReportsCompare({
        params,
        divisionId,
        data: safeData as { calculations?: string[], compareIds?: string[] }
      })
      return res
    },
    onError: (error: any) => {
      notification('Results for this survey will be available on :', { variant: 'error', subTitle: error?.message?.releaseDate })
    },
    onSuccess: (data: any, payload) => { }
  })

  const { data: completionRate = null, refetch: refetchCompletionRate, isError: isCompletionRateError, error: completionRateError, isLoading: completionRateLoading } = useQuery({
    queryKey: ['completionRate', id],
    staleTime: 10000,
    queryFn: async () => (await DivisionManager.getCompletionRate(divisionId, params)),
    enabled: false,
    retry: false
  })

  const downloadFile = async (fileType: string): Promise<void> => {
    const divisionId = breadCrumbData?.[breadCrumbData.length - 1]?.id
    if (!divisionId) return

    try {
      const fetchFile = fileType === 'csv'
        ? DivisionManager.getDivisionCSVExport
        : DivisionManager.getDivisionPDFExport

      const result = await fetchFile(divisionId, params)
      const blob = new Blob([result], {
        type: fileType === 'csv' ? 'text/csv' : 'application/pdf'
      })

      saveAs(blob, `report-${divisionId}.${fileType}`)
    } catch (err) {
      notification(`${fileType.toUpperCase()} export error`, {
        variant: 'error',
        subTitle: `An error occurred while trying to export the ${fileType.toUpperCase()}`
      })
    }
  }

  useEffect(() => {
    const FALLBACK = 'Unknown error occurred'
    const reportStatus = reportError?.status
    const rateStatus = completionRateError?.status

    if (!reportStatus && !rateStatus && isPending && completionRateLoading) return

    const { BAD_REQUEST, NOT_FOUND, UNPROCESSABLE_ENTITY } = HttpStatusCode

    // Both not found
    if (
      reportStatus === NOT_FOUND &&
      rateStatus === NOT_FOUND &&
      isReportsError &&
      isCompletionRateError
    ) {
      notification(formatErrorMessage(reportError?.message, FALLBACK), { variant: 'error' })
      return
    }

    // Bad request and unprocessable entity
    if (
      reportStatus === BAD_REQUEST &&
      rateStatus === UNPROCESSABLE_ENTITY &&
      isReportsError &&
      isCompletionRateError
    ) {
      notification(formatErrorMessage(completionRateError?.message, FALLBACK), { variant: 'error' })
      return
    }

    // Only report is bad request
    if (
      reportStatus === BAD_REQUEST &&
      isReportsError &&
      rateStatus !== UNPROCESSABLE_ENTITY
    ) {
      notification(formatErrorMessage(reportError?.message, FALLBACK), { variant: 'info' })
      return
    }

    // Only report is not found
    if (reportStatus === NOT_FOUND && isReportsError) {
      notification(formatErrorMessage(reportError?.message, FALLBACK), { variant: 'error' })
      return
    }

    // Only report is unprocessable entity
    if (
      reportStatus === UNPROCESSABLE_ENTITY &&
      isReportsError &&
      !isCompletionRateError
    ) {
      notification(formatErrorMessage(reportError?.message, FALLBACK), { variant: 'error' })
    }
  }, [
    reportError?.status,
    completionRateError?.status,
    isReportsError,
    isCompletionRateError
  ])

  const handleTermsHeader = (): void => {
    void refetchCompletionRate()
    fetchReports({})
  }

  useEffect(() => {
    setInitialLoading(true)
  }, [id])

  useEffect(() => {
    if ((initialLoading && terms && distributionSeries && divisionId && seriesId && termId)) {
      handleTermsHeader()
      setInitialLoading(false)
    }
  }, [seriesId, termId, divisionId, terms, id, initialLoading])

  // Add loader for mutation API
  if (breadcrumbLoading || distributionSeriesLoading) {
    return <LoadingPageV2 title={PAGE_TITLES.REPORTS} />
  }

  const loading = completionRateLoading || initialLoading || isPending

  const controls = <ReportsControls
    disableButtons={!completionRate}
    downloadPDFReport={async () => { await downloadFile('pdf') }}
    downloadCSVFile={async () => { await downloadFile('csv') }}
    // settings={settings}
    reports={isReportsError ? [] : reports}
    fetchReports={fetchReports}
    setIsFullScreen={setIsFullScreen}
    isFullScreen={isFullScreen}
  />

  const renderContent = (): ReactNode => {
    if (loading) {
      return <LoadingPageV2 title={PAGE_TITLES.REPORTS} />
    }
    if (completionRate && !isCompletionRateError) {
      return (
        <ReportsFeature
          reports={reports}
          completionRate={completionRate}
          reportError={reportError}
          parentSurveyScore={completionRate?.surveyScore}
        />
      )
    }

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <EmptyListCard
        // message={errorMessage}
        />
      </Box>
    )
  }

  return (
    <>
      <CustomHeader info={preparedBreadCrumbList?.at(-1)?.title ?? PAGE_TITLES.REPORTS} />
      <SkipNavigation />
      <Container
        maxWidth={isFullScreen ? false : 'lg'}
        disableGutters={isFullScreen}
      >
        <Stack>
          <PageHeader
            breadcrumb={preparedBreadCrumbList}
            actions={
              <TermHeader
                onClick={handleTermsHeader}
                distributionSeries={distributionSeries}
                terms={terms}
                termsLoading={termsLoading}
                seriesLoading={distributionSeriesLoading} />
            }
            attributes={completionRate && <ReportAttributes completionRate={completionRate} parentSurveyScore={completionRate?.surveyScore} />}
            title={preparedBreadCrumbList?.at(-1)?.title ?? PAGE_TITLES.REPORTS}
            controls={controls}
          />
          {renderContent()}
        </Stack>
      </Container>
    </>
  )
}

export default ReportsPage
